import React, { useContext } from 'react'
import { Link } from 'react-router-dom'

import { ROUTE_TYPE } from '../../Configuration'
import { DueOut } from '../../assets/ts/data/dueout/dueout'
import { STATUS_CODE } from '../../assets/ts/data/dueout/dueout-status-metadata'
import { convertDate, localDateToUTCDate } from '../../assets/ts/date-and-time/date-and-time'
import { defaultToEmptyString } from '../../assets/ts/formatting/formatting'
import { handleScroll, toShortDateString } from '../../assets/ts/general/general'
import { SelectionContext } from '../../store/selection-context-provider'
import { DUEOUT_STATUS } from '../../utils/constants/global-constants'
import { PersonDetails } from '../../utils/constants/global-interfaces'
import NO_RESULTS from '../../utils/constants/no-results'
import InputCheckbox from '../../utils/controls/input-checkbox'
import './table.css'

type Props = {
  deleteItems: (selectedItem: string) => Promise<void>
  dueOuts: DueOut[]
  onBottomScrollReached?: () => void
  onTopScrollReached?: () => void
  title: string
}

function Row(
  dueouts: DueOut[],
  selection,
  deleteItems: (selectedItem: string) => Promise<void>,
) {
  if (dueouts.length > 0) {
    return dueouts?.map((dueout: DueOut) => (
      <>
        <tr key={dueout.DueOutGUID}>
            <th className='checkbox'>
              <InputCheckbox
                formClassName='form-horizontal font-0'
                inputProps={{
                  checked: selection.keyIsSelected(dueout.DueOutGUID),
                  id: dueout.DueOutGUID,
                  label: `checkbox-${dueout.DueOutID}`,
                  name: 'Input Check',
                  type: 'checkbox',
                }}
                onChange={(e) => selection.handleSingleCheckboxChange(e, dueout.DueOutGUID ?? '')}
              />
            </th>
          <th aria-label={dueout.DueOutID} tabIndex={0}>
            <Link
              className='links'
              state={{ prevPath: '/myinquiries-dueouts' }}
              title={dueout.DueOutID}
              to={
                dueout?.StatusCode === STATUS_CODE.SAVED
                  ? `${ROUTE_TYPE}createdueout/${dueout.DueOutGUID}`
                  : `${ROUTE_TYPE}dueoutdetailview/${dueout.DueOutGUID}`
              }
            >
              {defaultToEmptyString(dueout.DueOutID)}
            </Link>
          </th>
          <td aria-label={dueout.SectionValue} tabIndex={0}>
            {defaultToEmptyString(dueout.SectionValue)}
          </td>
          <td aria-label={dueout.CategoryValue} tabIndex={0}>
            {defaultToEmptyString(dueout.CategoryValue)}
          </td>
          <td aria-label={dueout.SubCategoryValue} tabIndex={0}>
            {defaultToEmptyString(dueout.SubCategoryValue)}
          </td>
          <td aria-label={dueout.ServicingBy} tabIndex={0}>
            {defaultToEmptyString(dueout.ServicingBy)}
          </td>
          <td aria-label={dueout.Priority} tabIndex={0}>
            {defaultToEmptyString(dueout.Priority)}
          </td>
          <td aria-label={toShortDateString(dueout.ItemCreated)} tabIndex={0}>
            {defaultToEmptyString(toShortDateString(dueout.ItemCreated))}
          </td>
          <td
            aria-label={defaultToEmptyString(toShortDateString(
              dueout.DueDate === null 
              ? undefined : convertDate(localDateToUTCDate(dueout.DueDate), '') 
            ))}
            tabIndex={0}
          >
            {defaultToEmptyString(toShortDateString(
            dueout.DueDate === null 
            ? undefined : convertDate(localDateToUTCDate(dueout.DueDate), '') 
          ))}
          </td>
          <th
            aria-label={dueout.Status}
            data-table-status={DUEOUT_STATUS[dueout.StatusCode ?? '']?.colorCode}
            tabIndex={0}
          >
            {defaultToEmptyString(dueout.Status)}
          </th>
          {dueout?.StatusCode === STATUS_CODE.SAVED && (
            <td aria-label='delete' tabIndex={0} title='delete'>
              <span
                className='icon-trashbin font-12 color-primary cursor'
                onClick={() => deleteItems(dueout.DueOutGUID)}
              ></span>
            </td>
          )}
        </tr>
        <tr>
          <th colSpan={12}>
            <div className='d-flex align-items-center'>
              <Link
                className='links'
                state={{ prevPath: '/myinquiries-dueouts' }}
                title={dueout.Subject}
                to={
                  dueout?.StatusCode === STATUS_CODE.SAVED
                    ? `${ROUTE_TYPE}dueout/${dueout.DueOutGUID}`
                    : `${ROUTE_TYPE}dueoutdetailview/${dueout.DueOutGUID}`
                }
              >
                {defaultToEmptyString(dueout.Subject)}
              </Link>
              <div className='dividerdashed vertical mx-2'></div>
              <div
                aria-label={(dueout.Customer as PersonDetails)?.properties?.defaultDisplay}
                className='m-0 p-0 userinfo title-color15 font-11 text-uppercase poppins-semibold'
                tabIndex={0}
              >
                <span className='icon-user '></span>
                {defaultToEmptyString(
                  (dueout.Customer as PersonDetails)?.properties?.defaultDisplay
                )}
              </div>
            </div>
          </th>
        </tr>
      </>
    ))
  } else {
    return (
      <tr className='no-results'>
        <td colSpan={10}>
          <NO_RESULTS></NO_RESULTS>
        </td>
      </tr>
    )
  }
}

function getTableHeaderHeight() {
  const thead = document.querySelector('#tableFixHead thead') as HTMLElement | null
  return thead ? thead.offsetHeight : 0
}

function updateTableHeaderHeight() {
  const tableHeader = getTableHeaderHeight()
  document.documentElement.style.setProperty('--tableHeader', `${tableHeader + 16}px`)
}
updateTableHeaderHeight()
window.addEventListener('resize', updateTableHeaderHeight)

const MyDueoutsTable = ({
  deleteItems,
  dueOuts,
  onBottomScrollReached,
  onTopScrollReached,
}: Props) => {
  const selection = useContext(SelectionContext)

  return (
    <div
      className='w-100 overflow-auto px-1 inquiriestable'
      id='scrolltable'
      onScroll={(e) => handleScroll(e, onTopScrollReached, onBottomScrollReached)}
    >
      <table
        className='table-borderless table-dis w-100 sticky-top second-column last-column table-rowgap'
        id='tableFixHead'
      >
        <thead>
          <tr>
              <th>
                <InputCheckbox
                  formClassName='form-horizontal font-0'
                  inputProps={{
                    checked: selection.allCheckboxIsChecked,
                    id: 'all',
                    label: 'All',
                    name: 'Input Check',
                    type: 'checkbox',
                  }}
                  onChange={selection.handleAllCheckboxChange}
                />
              </th>
            <th aria-label='ID' tabIndex={0}>
              ID
            </th>
            <th aria-label='Section' tabIndex={0}>
              Section
            </th>
            <th aria-label='Category' tabIndex={0}>
              Category
            </th>
            <th aria-label='Sub Category' tabIndex={0}>
              Sub Category
            </th>
            <th aria-label='Current Servicing Unit' tabIndex={0}>
              Current Servicing Unit
            </th>
            <th aria-label='Priority' tabIndex={0}>
              Priority
            </th>
            <th aria-label='Status Modified Date' tabIndex={0}>
              Submitted Date
            </th>
            <th aria-label='Due Date' tabIndex={0}>
              Due Date
            </th>
            <th aria-label='Status' tabIndex={0}>
              Status
            </th>
            <th className='font-0'>Delete Icon</th>
          </tr>
        </thead>
        <tbody>{Row(dueOuts, selection, deleteItems)}</tbody>
      </table>
    </div>
  )
}
export default MyDueoutsTable
